<template>
    <b-modal
        ref="dialog"
        title-tag="h2"
        :lazy="true"
        @hidden="close"
        :title="title"
        no-close-on-backdrop
        no-close-on-esc
        no-enforce-focus
    >
        <div
            v-show="
                action === 'print' || (action === 'email' && currentPage === 1)
            "
        >
            <b-form-group
                label-cols="3"
                label="Veterinarian:"
                label-for="veterinarian"
            >
                <b-select
                    v-model="veterinarian"
                    :disabled="isLoadingVeterinarian"
                    :options="veterinarianOptions"
                />
            </b-form-group>

            <b-form-group label-cols="3" label="From:" label-for="from">
                <div class="date-wrapper">
                    <div>
                        <date-picker
                            @dp-change="
                                value => refetchFilters('fromDate', value.date)
                            "
                            :config="{ format: getDateFormat() }"
                            v-model="fromDate"
                            placeholder="Start date (required)"
                            v-validate="'required'"
                            name="fromDate"
                            :class="{
                                'border-danger': formError.includes('fromDate')
                            }"
                        />
                    </div>
                    <b>To:</b>
                    <div>
                        <date-picker
                            @dp-change="
                                value => refetchFilters('toDate', value.date)
                            "
                            :config="{ format: getDateFormat() }"
                            v-model="toDate"
                            placeholder="End date (required)"
                            v-validate="'required'"
                            name="toDate"
                            :class="{
                                'border-danger': formError.includes('toDate')
                            }"
                        />
                    </div>
                </div>
            </b-form-group>

            <b-form-group
                label-cols="3"
                label="Include:"
                label-for="include"
                ref="include"
            >
                <loader :margin="0" v-if="isLoadingFilter" />
                <div v-else class="checkbox-wrapper">
                    <label>
                        <input
                            type="checkbox"
                            v-model="includeAll"
                            @change="selectAllFilter"
                        />
                        Include All
                    </label>
                    <span v-for="filter in filters" :key="filter.id">
                        <label
                            :class="{
                                'is-children': filter.type === 'child',
                                'is-lower-children':
                                    filter.type === 'lower-child'
                            }"
                        >
                            <input
                                @change="
                                    event => selectItemFilter(event, filter)
                                "
                                type="checkbox"
                                v-model="selectedFilters"
                                :value="filter.id"
                            />
                            {{ filter.label }}
                        </label>
                        <template
                            v-if="
                                filter.id == 'cb[cb_attachment]' &&
                                attachmentSelected
                            "
                        >
                            <b-form-group
                                class="is-attachment"
                                label-cols="4"
                                label="Applies to:"
                                label-for="appliesTo"
                            >
                                <b-select
                                    v-model="appliesTo"
                                    :options="attachmentApplyOption"
                                />
                            </b-form-group>
                            <b-form-group
                                v-if="appliesTo == 2"
                                class="is-attachment d-flex flex-column"
                                label-cols="4"
                                label="Attachment(s):"
                                label-for="attachments"
                            >
                                <v-select
                                    id="attachments"
                                    ref="vselect"
                                    v-model="attachments"
                                    :class="{
                                        'border-danger': attachments.length == 0
                                    }"
                                    class="v-select-custom"
                                    :options="attachmentsList"
                                    name="attachments"
                                    placeholder="Attachment(s) (required)"
                                    label="title"
                                    multiple
                                >
                                    <template slot="no-options">
                                        <span>No matches found</span>
                                    </template>
                                </v-select>
                            </b-form-group>
                        </template>
                    </span>
                </div>
            </b-form-group>
        </div>

        <div v-show="action === 'email' && currentPage === 2">
            <b-form-group label-cols="3" label="To:" label-for="to">
                <recipient-email
                    v-model="recipients"
                    name="recipients"
                    multiple
                    placeholder="Client or Contact Email (required)"
                    @input="handleRecipientChange"
                    :key="recipientComponentKey"
                    :client="clientDetails"
                    v-validate="'required'"
                    :class="{
                        'border-danger': formError.includes('recipients')
                    }"
                />
            </b-form-group>

            <b-form-group label-cols="3" label="Subject:" label-for="subject">
                <b-input
                    v-model="subject"
                    :disabled="isLoadingTemplate"
                    :placeholder="
                        isLoadingTemplate ? 'Loading...' : 'Subject (optional)'
                    "
                />
            </b-form-group>

            <b-form-group label-cols="3" label="Message:" label-for="message">
                <tinymce
                    :init="tinymceConfig"
                    v-model="message"
                    @onKeyDown="tinymceKeydown"
                />

                <label class="cc">
                    <input type="checkbox" v-model="ccEmail" />
                    Send me a copy of this message
                </label>
            </b-form-group>
        </div>

        <template v-slot:modal-footer>
            <loader v-if="isLoading" :margin="1" />

            <b-button
                class="mr-auto"
                variant="secondary"
                @click="close"
                v-if="!isLoading"
            >
                Cancel
            </b-button>

            <b-button
                v-if="!isLoading && action === 'print'"
                variant="primary"
                @click="submit(EXPORT_TYPE_PDF)"
                :disabled="selectedFilters.length <= 0"
            >
                Preview
            </b-button>

            <b-button
                v-if="!isLoading && emailPage1"
                variant="primary"
                @click="next"
                :disabled="selectedFilters.length <= 0"
            >
                Next
            </b-button>

            <b-button
                v-if="!isLoading && emailPage2"
                variant="primary"
                @click="prev"
            >
                Prev
            </b-button>
            <b-button
                v-if="!isLoading && emailPage2"
                variant="primary"
                @click="submit(EXPORT_TYPE_EMAIL)"
            >
                Email File
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import axios from "axios";
import moment from "moment";
import Loader from "@/module/commons/loader";
import RecipientEmail from "@/components/commons/recipients-email";
import tinymce from "@tinymce/tinymce-vue";
import tinymceHelper from "@/module/commons/helper-tinymce"; // helper
import ModalExport from "@/mixins/modal-export.js";
import vSelect from "vue-select";
const EXPORT_TYPE_EMAIL = 1;
const EXPORT_TYPE_PDF = 3;
/**
 * @component modal-export
 * @description This component been used by patient profile only
 *              As the functions mostly used by modal-export-soap, the reusable function has been moved to mixins/modal-export.js
 */
export default {
    mixins: [ModalExport],
    components: {
        Loader,
        RecipientEmail,
        tinymce,
        vSelect
    },
    props: {
        patientDetails: {
            type: Object
        }
    },
    data() {
        return {
            EXPORT_TYPE_EMAIL,
            EXPORT_TYPE_PDF,
            title: "Export",
            action: "",
            isLoading: false,
            currentPage: 1, // print has only 1 page, email has 2 pages
            veterinarian: "",
            veterinarianOptions: [{ text: "Loading...", value: "" }],
            isLoadingVeterinarian: true,
            formError: [],
            fromDate: "",
            toDate: "",
            isLoadingFilter: true,
            filters: [],
            selectedFilters: [],
            includeAll: false,
            recipients: [],
            recipientComponentKey: 0,
            clientDetails: null,
            subject: "",
            isLoadingTemplate: true,
            message: "",
            ccEmail: false,
            tinymceConfig: {
                ...tinymceHelper.core, // use spread operator to inject all vetter-related configs
                toolbar: false,
                plugins: tinymceHelper.plugins
            },
            initialContentChangedByUser: false,
            isNextRequireFetch: true,
            attachmentSelected: false,
            attachmentApplyOption: [
                { value: 1, text: "All attachment(s)" },
                { value: 2, text: "Specific attachment(s)" }
            ],
            appliesTo: 1,
            attachments: [],
            attachmentOption: []
        };
    },
    watch: {
        selectedFilters(val) {
            this.attachmentSelected = val.includes("cb[cb_attachment]");
            if (!val.includes("cb[cb_attachment]")) {
                this.appliesTo = 1;
                this.attachments = [];
            }
        },
        appliesTo() {
            this.attachments = [...this.attachmentOption];
        }
    },
    computed: {
        attachmentsList() {
            let attachmentOption = this.attachmentOption.filter(
                item => !this.attachments.includes(item)
            );
            return attachmentOption;
        }
    },
    methods: {
        refetchFilters(key, momentDate) {
            let date = momentDate.format(this.getDateFormat());

            if (key === "fromDate") {
                this.fetchFilter(date, this.toDate);
            } else if (key === "toDate") {
                this.fetchFilter(this.fromDate, date);
            }
        },
        open(params = {}) {
            const { action } = params;

            if (action === "print") {
                this.title = "Print File";
            } else if (action === "email") {
                this.title = "Email File";
            }

            this.action = action;
            this.$refs.dialog.show();
            this.fetchVeterinarians();
            this.initDateTimeFilter();
        },
        async next() {
            let valid = true;
            if (this.attachmentSelected) {
                valid = this.attachments.length > 0;
            }
            if (valid) {
                this.currentPage = 2; // no need to ++, because we have 2 pages only

                if (this.isNextRequireFetch) {
                    this.fetchTemplate(true);
                }
            }
        },
        initDateTimeFilter() {
            let start = moment
                .tz(this._timeZone)
                .startOf("month")
                .format(this.getDateFormat());

            let end = moment.tz(this._timeZone).format(this.getDateFormat());

            this.fromDate = start;
            this.toDate = end;
            this.fetchFilter(start, end);
        },
        async fetchFilter(start, end) {
            let exportType;

            if (this.action === "email") {
                exportType = "1";
            } else if (this.action === "print") {
                exportType = "3";
            }

            try {
                this.isLoadingFilter = true;

                const res = await axios.get(
                    `${process.env.CI_APPS}patient/file/filter_export_elements`,
                    {
                        params: {
                            id: this.patientId,
                            start_date: start,
                            end_date: end,
                            export_type: exportType
                        }
                    }
                );

                if (res.data.success) {
                    this.isLoadingFilter = false;
                    this.filters = this.properFilter(res.data.resource);
                } else {
                    throw new Error("Unable to fetch filter list.");
                }
            } catch (e) {
                this._message(e.message, "danger");
                this.isLoadingFilter = false;
            }
        },
        async submit(exportType) {
            let valid = true;
            if (exportType == 1) {
                valid = await this.$validator.validateAll();
            }
            if (this.attachmentSelected) {
                valid = this.attachments.length > 0;
            }
            if (valid) {
                try {
                    let primaryOwner = this.patientDetails.primary_owner;
                    let attachmentFiles = [];
                    if (this.attachmentSelected) {
                        attachmentFiles = this.attachments.map(
                            attachment => attachment.id
                        );
                    }
                    let owner =
                        primaryOwner && primaryOwner[0]
                            ? primaryOwner[0]
                            : { firstname: "", lastname: "" };

                    let formData = new FormData();
                    formData.append("id", this.patientId);
                    formData.append("export_type", exportType);
                    formData.append(
                        "email_type",
                        this.EMAIL_TYPE.PATIENT_HISTORY
                    );
                    formData.append("first_name", owner.firstname);
                    formData.append("last_name", owner.lastname);
                    formData.append("veterinarian", this.veterinarian);
                    formData.append("start_date", this.fromDate);
                    formData.append("end_date", this.toDate);
                    formData.append("subject", this.subject);
                    formData.append("message", this.message);

                    for (const file of attachmentFiles) {
                        formData.append("attachment_files[]", file);
                    }

                    if (this.ccEmail) {
                        formData.append("cc_email", 1);
                    }

                    formData.append(
                        "timestamp",
                        Math.floor(new Date().getTime() / 1000)
                    );

                    for (const rec of this.recipients) {
                        formData.append("to[]", rec.to_id);
                    }

                    for (const filterId of this.selectedFilters) {
                        formData.append(filterId, "on");
                    }

                    if (this.includeAll) {
                        formData.append("cb[cb_include_all]", "on");
                    }

                    this.isLoading = true;

                    const res = await axios.post(
                        `${process.env.CI_APPS}patient/file/export_file`,
                        formData
                    );

                    if (Number(res.data.state) === 0) {
                        this.isLoading = false;

                        let message = res.data.message;
                        if (res.data.resource && res.data.resource.message) {
                            message = res.data.resource.message;
                        }

                        this._message(message);
                        this.$emit("onsuccess");
                        this.close();

                        // print
                        if (exportType === 3) {
                            if (res.data.resource && res.data.resource.path) {
                                window.open(res.data.resource.path);
                            }
                        }
                    } else {
                        throw new Error(res.data.message);
                    }
                } catch (e) {
                    this.isLoading = false;
                    this._message(e.message, "danger");
                }
            }
        },
        async fetchTemplate(setRecipient = true) {
            try {
                const settings = await this._getSetting();
                // const { id: patientId } = this.patientDetails;
                let patientId = this.patientDetails.id;
                let primaryOwner = this.patientDetails.primary_owner;
                let owner;
                let isNoOwner = false;

                if (primaryOwner && primaryOwner[0]) {
                    owner = primaryOwner[0];
                } else {
                    owner = this._setting.clinic_default_owner;
                    isNoOwner = true;
                }

                const formData = new FormData();
                formData.append(
                    "params[0][email_type]",
                    this.EMAIL_TYPE.PATIENT_HISTORY
                );
                formData.append("params[0][business_id]", settings.business_id);
                formData.append("params[0][patient_id][]", patientId);

                if (owner) {
                    formData.append("params[0][client_id]", owner.id);
                }

                if (isNoOwner) {
                    formData.append("params[0][use_clinic_default_owner]", 1);
                }

                this.isLoadingTemplate = true;

                const res = await axios.post(
                    `${process.env.CI_APPS}common/appointment/getEmailTemplate`,
                    formData
                );

                if (
                    res.data.response &&
                    res.data.response[0] &&
                    res.data.response[0].email_template
                ) {
                    this.isLoadingTemplate = false;

                    const {
                        subject,
                        message
                    } = res.data.response[0].email_template;

                    this.subject = subject;
                    this.message = message;
                    this.defaultMessage = message;

                    if (setRecipient) {
                        this.setDefaultRecipient();
                    }
                } else {
                    throw new Error("Unable to fetch email content.");
                }
            } catch (e) {
                this.isLoadingTemplate = false;
                this._message(e.message, "danger");
            }
        },
        async handleRecipientChange(values = []) {
            // if user clears up recipient field, reload the default content
            // but only if: user never change the initial content
            let self = this;
            if (values.length === 0 && !self.initialContentChangedByUser) {
                this.fetchTemplate(false);
            }
            // replace variables
            else {
                let recipients = [];
                for (const rec of values) {
                    recipients.push(rec.to_id);
                }

                let formData = new FormData();
                let msg = this.message || "";

                let header = msg.slice(0, msg.indexOf("</p>")); // <-- header memang akan dihantar ke server tanpa penutup </p>
                let body = msg.substring(msg.length, header.length);

                formData.append("recipients", recipients.join(","));
                formData.append("email_type", this.EMAIL_TYPE.PATIENT_HISTORY);
                formData.append("message_body", body);
                formData.append("header", header);

                const res = await axios.post(
                    `${process.env.CI_APPS}business/email_setting/change_recipient`,
                    formData
                );

                if (res.data && res.data.message) {
                    this.message = res.data.message || this.defaultMessage;
                }
            }
        },
        formatBody(rawBody = "") {
            const { name, email, phone } = this._setting.business;
            let msg = rawBody;
            msg = msg.replaceAll("{CLINIC_PHONE}", phone);
            msg = msg.replaceAll("{CLINIC_EMAIL}", email);
            msg = msg.replaceAll("{CLINIC_NAME}", name);
            return msg;
        }
    }
};
</script>
<style lang="scss" scoped>
@import "@/scss/modal-export.scss";
</style>
<style scoped>
.is-attachment {
    margin-left: 20px;
}
.v-select-custom >>> .vs__dropdown-toggle {
    max-width: 23em; /* Restrict the width */
    overflow-x: scroll; /* Prevent expansion */
}
</style>
